import { useEffect } from 'react';

/**
 * Scroll a tooltip into view when it becomes visible.
 *
 * @param {boolean} isVisible - Whether the tooltip is visible.
 * @param {string|Element|null} target - A CSS selector string or a DOM element for the tooltip root.
 * @param {Object} options - Optional configuration.
 * @param {number} options.delayMs - Delay before measuring/scrolling (default: 300ms).
 * @param {number} options.idealViewportFraction - Position target fraction of viewport height (default: 1/3).
 * @param {number} options.bottomPaddingPx - Extra padding when bottom is hidden (default: 20px).
 */
const useScrollTooltipIntoView = (
  isVisible,
  target,
  { delayMs = 300, idealViewportFraction = 1 / 3, bottomPaddingPx = 20 } = {}
) => {
  useEffect(() => {
    if (!isVisible) return undefined;

    const timer = setTimeout(() => {
      const tooltipElement = typeof target === 'string' ? document.querySelector(target) : target;

      if (!tooltipElement) return;

      const rect = tooltipElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const isFullyVisible = rect.top >= 0 && rect.bottom <= viewportHeight;

      if (isFullyVisible) return;

      const isBottomHidden = rect.bottom > viewportHeight;
      const idealPosition = viewportHeight * idealViewportFraction;

      if (rect.height < viewportHeight) {
        const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
        let newScrollTop;

        if (isBottomHidden && rect.top >= 0) {
          newScrollTop = currentScroll + (rect.bottom - viewportHeight) + bottomPaddingPx;
        } else {
          newScrollTop = currentScroll + rect.top - idealPosition;
        }

        window.scrollTo({ top: newScrollTop, behavior: 'smooth' });
      } else {
        tooltipElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, delayMs);

    return () => clearTimeout(timer);
  }, [isVisible, target, delayMs, idealViewportFraction, bottomPaddingPx]);
};

export default useScrollTooltipIntoView;
